import { useState } from 'react';
import { Button } from '../Button';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  approveKioskRegistration,
  cancelKioskRegistration,
  markKioskRegistrationCallCompleted,
  markKioskRegistrationPaymentReceived,
  markKioskRegistrationTermsSigned,
  rejectKioskRegistration,
  resendKioskRegistrationWelcome,
} from '../../services/KioskRegistrationService';
import type {
  KioskRegistration,
  KioskRegistrationAction,
} from '../../types/kioskRegistration.types';
import ModalShell from './ModalShell';
import SendProposalModal from './SendProposalModal';
import { ACTION_LABELS, INPUT_CLASS } from './registrationUi';

interface Props {
  registration: KioskRegistration;
  action: KioskRegistrationAction;
  onClose: () => void;
  onDone: () => void;
}

type FieldConfig = {
  description: string;
  textLabel?: string;
  textRequired?: boolean;
  textPlaceholder?: string;
  submitLabel: string;
};

const CONFIG: Partial<Record<KioskRegistrationAction, FieldConfig>> = {
  APPROVE: {
    description: 'Approve this registration. Any approval gates shown on the registration must be satisfied.',
    textLabel: 'Note (optional)',
    submitLabel: 'Approve',
  },
  REJECT: {
    description: 'Reject this registration. The reason is stored in the audit trail.',
    textLabel: 'Reason',
    textRequired: true,
    submitLabel: 'Reject',
  },
  CANCEL: {
    description: 'Cancel this registration. The reason is stored in the audit trail.',
    textLabel: 'Reason',
    textRequired: true,
    submitLabel: 'Cancel registration',
  },
  MARK_CALL_COMPLETED: {
    description: 'Record that the onboarding call took place.',
    textLabel: 'Call notes (optional)',
    submitLabel: 'Mark call completed',
  },
  MARK_TERMS_SIGNED: {
    description:
      'Confirms the PandaDoc Terms & Conditions are signed. This creates the owner account and channel and issues the setup invoice (or opens the channel immediately when there is no onboarding fee).',
    textLabel: 'Document reference (optional)',
    textPlaceholder: 'PandaDoc document id',
    submitLabel: 'Mark T&Cs signed',
  },
  MARK_PAYMENT_RECEIVED: {
    description:
      'Marks the setup invoice as paid, opens the channel and sends the welcome email to the owner.',
    textLabel: 'Notes (optional)',
    submitLabel: 'Mark payment received',
  },
  RESEND_WELCOME: {
    description: 'Send the welcome email to the owner again.',
    submitLabel: 'Resend welcome email',
  },
};

const SUCCESS_MESSAGES: Record<KioskRegistrationAction, string> = {
  APPROVE: 'Registration approved.',
  REJECT: 'Registration rejected.',
  CANCEL: 'Registration cancelled.',
  MARK_CALL_COMPLETED: 'Call marked as completed.',
  SEND_PROPOSAL: 'Proposal email sent.',
  MARK_TERMS_SIGNED: 'Terms marked as signed.',
  MARK_PAYMENT_RECEIVED: 'Payment recorded.',
  RESEND_WELCOME: 'Welcome email resent.',
};

export default function RegistrationActionModal({ registration, action, onClose, onDone }: Props) {
  const [text, setText] = useState('');
  const [notifyApplicant, setNotifyApplicant] = useState(false);
  const [signedAt, setSignedAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (action === 'SEND_PROPOSAL') {
    return <SendProposalModal registration={registration} onClose={onClose} onDone={onDone} />;
  }

  const config = CONFIG[action];
  if (!config) return null;

  const submit = async () => {
    const value = text.trim();
    if (config.textRequired && !value) {
      setError('A reason is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const id = registration.id;
      switch (action) {
        case 'APPROVE':
          await approveKioskRegistration(id, { note: value || undefined });
          break;
        case 'REJECT':
          await rejectKioskRegistration(id, { reason: value, notifyApplicant });
          break;
        case 'CANCEL':
          await cancelKioskRegistration(id, { reason: value });
          break;
        case 'MARK_CALL_COMPLETED':
          await markKioskRegistrationCallCompleted(id, { notes: value || undefined });
          break;
        case 'MARK_TERMS_SIGNED':
          await markKioskRegistrationTermsSigned(id, {
            documentReference: value || undefined,
            signedAt: signedAt ? new Date(signedAt).toISOString() : undefined,
          });
          break;
        case 'MARK_PAYMENT_RECEIVED':
          await markKioskRegistrationPaymentReceived(id, { notes: value || undefined });
          break;
        case 'RESEND_WELCOME':
          await resendKioskRegistrationWelcome(id);
          break;
        default:
          return;
      }
      showSuccessToast(SUCCESS_MESSAGES[action]);
      onDone();
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, 'The action could not be completed.');
      setError(message);
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title={`${ACTION_LABELS[action]} — ${registration.referenceCode}`}
      onClose={submitting ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button size="md" onClick={() => void submit()} loading={submitting}>
            {config.submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm text-gray-700 dark:text-gray-200">
        <p>{config.description}</p>
        {config.textLabel && (
          <label className="block font-medium">
            {config.textLabel}
            {action === 'MARK_TERMS_SIGNED' ? (
              <input
                value={text}
                onChange={(event) => setText(event.target.value)}
                maxLength={255}
                placeholder={config.textPlaceholder}
                className={`mt-1.5 ${INPUT_CLASS}`}
              />
            ) : (
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                maxLength={action === 'MARK_CALL_COMPLETED' ? 5000 : 1000}
                rows={4}
                required={config.textRequired}
                className={`mt-1.5 ${INPUT_CLASS}`}
              />
            )}
          </label>
        )}
        {action === 'MARK_TERMS_SIGNED' && (
          <label className="block font-medium">
            Signed at (optional)
            <input
              type="datetime-local"
              value={signedAt}
              onChange={(event) => setSignedAt(event.target.value)}
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </label>
        )}
        {action === 'REJECT' && (
          <label className="flex items-center gap-2 font-medium">
            <input
              type="checkbox"
              checked={notifyApplicant}
              onChange={(event) => setNotifyApplicant(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Email the applicant
          </label>
        )}
        {error && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
